import {PresenterRotatorServicePromiseClient} from '@/proto/presenter-rotator_grpc_web_pb'
import {
    AddPresenterRequest,
    ChangePresenterFieldRequest,
    ChangeTeamFieldRequest,
    CreateMeetingTypeRequest,
    EmailRequest,
    GetMeetingTypesRequest,
    GrantMeetingTypeAccessRequest,
    GrantTeamAccessRequest,
    LoginRequest,
    LogoutRequest,
    Name,
    Nothing,
    RefreshTokenRequest,
    RegisterRequest,
    RemovePresenterRequest,
    RequestPasswordResetRequest,
    ResetPasswordRequest,
    RevokeMeetingTypeAccessRequest,
    RevokeTeamAccessRequest,
    SetUserAdminRequest,
    TeamID,
    TeamIDAndMeetingTypeRequest,
    TeamIDRequest,
    ToggleOnVacationStateRequest,
    UpdateMeetingTypeScheduleRequest,
    UpdateMeetingTypesRequest,
} from '../proto/presenter-rotator_pb'
import {useTokenStore} from "@/stores/token";

let onUnauthorized = () => {};

export function setUnauthorizedHandler(handler) {
    onUnauthorized = handler;
}

function mapUser(user) {
    if (!user) {
        return null;
    }
    return {
        id: user.getId(),
        email: user.getEmail(),
        name: user.getName(),
        isAdmin: user.getIsAdmin(),
        teams: user.getTeamsList().map(team => ({
            teamId: team.getTeamId(),
            role: team.getRole(),
            teamName: team.getTeamName(),
        })),
    };
}

export default class {
    client

    constructor() {
        this.client = new PresenterRotatorServicePromiseClient(this.getBackendAddress(), null, null)
    }

    getBackendAddress() {
        let url = process.env.VUE_APP_BACKEND_ADDRESS;
        const port = process.env.VUE_APP_BACKEND_PORT || '80';
        const backedEndpoint = process.env.BACKEND_ENDPOINT;
        if (backedEndpoint !== undefined) {
            url = backedEndpoint
        }
        if (port !== '80') {
            url = url + ':' + port
        }
        return url;
    }

    metadata() {
        const token = useTokenStore().getAccessToken();
        if (!token || token === 'null') {
            return {};
        }
        return {authorization: 'Bearer ' + token};
    }

    isUnauthenticated(err) {
        return err && err.code === 16;
    }

    async refreshSession() {
        const store = useTokenStore();
        const refreshToken = store.getRefreshToken();
        if (!refreshToken) {
            return false;
        }
        const req = new RefreshTokenRequest();
        req.setRefreshToken(refreshToken);
        try {
            const response = await this.client.refreshToken(req, {});
            store.setSession(response.getAccessToken(), response.getRefreshToken(), mapUser(response.getUser()));
            return true;
        } catch (err) {
            return false;
        }
    }

    async call(method, request) {
        const skipRefresh = method === 'login' || method === 'register' || method === 'refreshToken' || method === 'requestPasswordReset' || method === 'resetPassword';
        try {
            return await this.client[method](request, this.metadata());
        } catch (err) {
            if (!skipRefresh && this.isUnauthenticated(err)) {
                const refreshed = await this.refreshSession();
                if (refreshed) {
                    return await this.client[method](request, this.metadata());
                }
                useTokenStore().clear();
                onUnauthorized();
            }
            console.error(err.message);
            throw err;
        }
    }

    async register(email, name, password) {
        const req = new RegisterRequest();
        req.setEmail(email);
        req.setName(name);
        req.setPassword(password);
        const response = await this.call('register', req);
        return {
            accessToken: response.getAccessToken(),
            refreshToken: response.getRefreshToken(),
            user: mapUser(response.getUser()),
        };
    }

    async login(email, password) {
        const req = new LoginRequest();
        req.setEmail(email);
        req.setPassword(password);
        const response = await this.call('login', req);
        return {
            accessToken: response.getAccessToken(),
            refreshToken: response.getRefreshToken(),
            user: mapUser(response.getUser()),
        };
    }

    async logout(refreshToken) {
        const req = new LogoutRequest();
        req.setRefreshToken(refreshToken || '');
        try {
            await this.client.logout(req, this.metadata());
        } catch (err) {
            console.error(err.message);
        }
    }

    async requestPasswordReset(email) {
        const req = new RequestPasswordResetRequest();
        req.setEmail(email);
        const response = await this.call('requestPasswordReset', req);
        return {
            success: response.getSuccess(),
            devResetToken: response.getDevResetToken(),
        };
    }

    async resetPassword(token, password) {
        const req = new ResetPasswordRequest();
        req.setToken(token);
        req.setPassword(password);
        const response = await this.call('resetPassword', req);
        return response.getSuccess();
    }

    async getCurrentUser() {
        const response = await this.call('getCurrentUser', new Nothing());
        return mapUser(response);
    }

    async findUserByEmail(email) {
        const req = new EmailRequest();
        req.setEmail(email);
        const response = await this.call('findUserByEmail', req);
        return mapUser(response);
    }

    async getTeamAccessList(teamId) {
        const req = new TeamID();
        req.setId(teamId);
        const response = await this.call('getTeamAccessList', req);
        return response.getMembersList().map(member => ({
            user: mapUser(member.getUser()),
            role: member.getRole(),
            meetingTypeIds: member.getMeetingTypeIdsList(),
        }));
    }

    async grantTeamAccess(userId, teamId, role) {
        const req = new GrantTeamAccessRequest();
        req.setUserId(userId);
        req.setTeamId(teamId);
        req.setRole(role);
        const response = await this.call('grantTeamAccess', req);
        return response.getSuccess();
    }

    async revokeTeamAccess(userId, teamId) {
        const req = new RevokeTeamAccessRequest();
        req.setUserId(userId);
        req.setTeamId(teamId);
        const response = await this.call('revokeTeamAccess', req);
        return response.getSuccess();
    }

    async grantMeetingTypeAccess(userId, meetingTypeId) {
        const req = new GrantMeetingTypeAccessRequest();
        req.setUserId(userId);
        req.setMeetingTypeId(meetingTypeId);
        const response = await this.call('grantMeetingTypeAccess', req);
        return response.getSuccess();
    }

    async revokeMeetingTypeAccess(userId, meetingTypeId) {
        const req = new RevokeMeetingTypeAccessRequest();
        req.setUserId(userId);
        req.setMeetingTypeId(meetingTypeId);
        const response = await this.call('revokeMeetingTypeAccess', req);
        return response.getSuccess();
    }

    async setUserAdmin(userId, isAdmin) {
        const req = new SetUserAdminRequest();
        req.setUserId(userId);
        req.setIsAdmin(isAdmin);
        const response = await this.call('setUserAdmin', req);
        return response.getSuccess();
    }

    async createTeam(teamName) {
        const req = new Name()
        req.setName(teamName)
        const res = await this.call('createTeam', req)
        return {
            id: res.getId(),
            name: res.getName(),
        }
    }

    async getTeams() {
        const data = await this.call('getTeams', new Nothing())
        return data.getTeamsList();
    }

    async createMeetingType(meetingType, teamId) {
        const req = new CreateMeetingTypeRequest()
        req.setName(meetingType)
        req.setTeamId(teamId)
        const res = await this.call('createMeetingType', req)
        return {
            id: res.getId(),
            name: res.getName(),
            teamId: res.getTeamId(),
        }
    }

    async getMeetingTypes(teamId) {
        const req = new GetMeetingTypesRequest();
        req.setTeamId(teamId);
        const data = await this.call('getMeetingTypes', req)
        return data.getTypesList()
    }

    async getMeetingTypesSchedule(teamId) {
        const meetingTypes = await this.getMeetingTypes(teamId)
        return meetingTypes.map(meetingType => ({
            id: meetingType.getId(),
            name: meetingType.getName(),
            team_id: meetingType.getTeamId(),
            rotation_days: meetingType.getRotationDaysList(),
            rotation_interval_weeks: meetingType.getRotationIntervalWeeks(),
            rotation_anchor_date: meetingType.getRotationAnchorDate(),
        }))
    }

    async updateMeetingTypeSchedule(meetingTypeId, schedule) {
        const req = new UpdateMeetingTypeScheduleRequest()
        req.setMeetingTypeId(meetingTypeId)
        req.setRotationDaysList(schedule.rotation_days)
        req.setRotationIntervalWeeks(schedule.rotation_interval_weeks)
        req.setRotationAnchorDate(schedule.rotation_anchor_date)
        const meetingType = await this.call('updateMeetingTypeSchedule', req)
        return {
            id: meetingType.getId(),
            name: meetingType.getName(),
            team_id: meetingType.getTeamId(),
            rotation_days: meetingType.getRotationDaysList(),
            rotation_interval_weeks: meetingType.getRotationIntervalWeeks(),
            rotation_anchor_date: meetingType.getRotationAnchorDate(),
        }
    }

    async createPresenter(name, messengerNickName, teamId) {
        const req = new AddPresenterRequest()
        req.setName(name)
        req.setMessengerNickName(messengerNickName)
        req.setTeamId(teamId)
        return await this.call('createPresenter', req)
    }

    async deletePresenter(presenterId) {
        const req = new RemovePresenterRequest()
        req.setPresenterId(presenterId)
        const result = await this.call('removePresenter', req)
        return result.getStatus()
    }

    async getPresenters(teamId) {
        const req = new TeamIDRequest();
        req.setTeamId(teamId);
        const data = await this.call('getPresenters', req)
        return data.getPresentersList();
    }

    async createMessenger(messengerName) {
        const req = new Name()
        req.setName(messengerName)
        const res = await this.call('createMessenger', req)
        return res.getName()
    }

    async getMessengers() {
        const data = await this.call('getMessengers', new Nothing())
        return data.getMessengersList()
    }

    async updateMeetingTypesForMember(memberId, meetingTypes) {
        const req = new UpdateMeetingTypesRequest();
        req.setPresenterId(memberId);
        req.setMeetingTypesList(meetingTypes);
        const data = await this.call('updateMeetingTypesForPresenter', req)
        return data.getSuccess()
    }

    async changePresenterField(presenterId, fieldName, value) {
        const req = new ChangePresenterFieldRequest();
        req.setPresenterId(presenterId);
        req.setField(fieldName);
        req.setValue(value);
        const data = await this.call('changePresenterField', req)
        return data.getSuccess()
    }

    async changeTeamField(teamId, fieldName, value) {
        const req = new ChangeTeamFieldRequest();
        req.setTeamId(teamId);
        req.setField(fieldName);
        req.setValue(value);
        const data = await this.call('changeTeamField', req)
        return data.getSuccess()
    }

    async getCurrentSprintName() {
        const sprintName = await this.call('getCurrentSprint', new Nothing())
        return sprintName.getSprint()
    }

    async getRotationDataForTeam(teamId) {
        const req = new TeamIDRequest();
        req.setTeamId(teamId);
        const data = await this.call('getRotationData', req)
        return data.getRotationsList()
    }

    async rotateTeamPresenters(teamId, sendNotification) {
        const req = new TeamIDRequest();
        req.setTeamId(teamId);
        req.setSendNotification(sendNotification);
        const data = await this.call('rotateTeamPresenters', req)
        return data.getRotationsList()
    }

    async rotateTeamPresentersForMeetingType(teamId, meetingTypeId, sendNotification) {
        const req = new TeamIDAndMeetingTypeRequest();
        req.setTeamId(teamId);
        req.setMeetingTypeId(meetingTypeId);
        req.setSendNotification(sendNotification);
        const data = await this.call('rotateTeamPresentersForMeetingType', req)
        return data.getRotationsList()
    }

    async toggleOnVacation(presenterId) {
        const req = new ToggleOnVacationStateRequest();
        req.setPresenterId(presenterId);
        return await this.call('toggleOnVacationState', req)
    }

}
